/**
 * Gallery item data — all gallery items grouped by category.
 *
 * Edit this file to add, remove, or modify items. Categories and their
 * display order are defined in src/categories.ts. Items with a `model`
 * definition show an embedded, auto-rotating 3D viewer when expanded.
 *
 * Every tile renders in one of two formats:
 *   a) gradient only         — set `gradient`, leave `imageSrc` unset
 *   b) image over a gradient — set both `imageSrc` and `gradient` (the
 *      gradient shows underneath the image as a loading placeholder and
 *      color base; the caption's own scrim keeps text legible)
 */

import * as THREE from 'three';
import { CategoryType } from './categories';
import { ModelDef } from './modelViewer';

export interface PhotoInfo {
  location?: string;
  date?: string;
  camera?: string;
  film?: string;
}

export interface GalleryItemDef {
  id: string;
  category: CategoryType;
  title: string;
  /** Short caption shown on the collapsed tile */
  description: string;
  /** Extra HTML shown only when the tile is expanded */
  details: string;
  /** Themed gradient (e.g. 'var(--tile-grad-1)') — tile background, or the base layer underneath `imageSrc` */
  gradient?: string;
  /** Optional photo/image shown on the tile, layered on top of `gradient` */
  imageSrc?: string;
  imageAlt?: string;
  /** External link shown in the expanded view */
  link?: string;
  /** 3D model shown in the expanded view, if any */
  model?: ModelDef;
  /** Percent of gallery-item height covered by the model canvas (0-100, default 100). */
  modelCanvasHeightPercent?: number;
  /** Photo metadata shown in the expanded info strip (photography category) */
  photoInfo?: PhotoInfo;
  /** Subcategory used for the photography filter buttons (e.g. 'Japan') */
  subcategory?: string;
  /** Path to a Markdown file (relative to the site root) rendered in the blog post overlay (blog category) */
  markdownPath?: string;
}

export const DEFAULT_MODEL_CANVAS_HEIGHT_PERCENT = 100;

export const galleryItems: GalleryItemDef[] = [
  {
    id: 'blog-hello-world',
    category: 'blog',
    title: 'Hello, Internet!',
    description: 'Kicking off my blog with an introduction to my newest renderer, Eos',
    gradient: 'var(--tile-grad-1)',
    details: '2026, July 26th',
    markdownPath: 'blog/hello-eos.md',
  },
  {
    id: 'graphics-lambda',
    category: 'graphics',
    title: 'Lambda Path Tracer',
    description: 'A SIMD-optimized spectral renderer',
    gradient: 'var(--tile-grad-1)',
    link: 'https://github.com/webstek/lambda',
    details: `
      <h3>Features:</h3>
      <ul>
        <li>Unidirectional importance tracing</li>
        <li>8-way wide BVH with AVX2-accelerated traversal</li>
        <li>Multiple importance sampling of BSDFs and area lights</li>
        <li>Instanced triangle meshes and materials</li>
        <li>Lambertian, Blinn, and thin-film materials</li>
        <li>Filmic tone mapping</li>
        <li>Supports measured, RGB-converted, and constant spectra</li>
      </ul>
      
      <h3>Renders:</h3>
      <img src="photos/renders/dispersion-16384spp-64b-0.90p.png" alt="Rendering showing dispersion" width=49%>
      <img src="photos/renders/box-1024spp-8b-0.80p-0.25Y-raw.png" alt="Cornell Box Rendering" width=49%>
      <img src="photos/renders/coverpage-524288spp-64b-0.90p.png" alt="Rendering showing off Lambda's capabilities" width=98.8%>
    `,
    model: {
      geometry: () => new THREE.BoxGeometry(1.6, 1.6, 1.6),
      material: () => new THREE.MeshPhysicalMaterial({
        color: 0xCE0000,
        metalness: 0.5,
        roughness: 0.3,
        ior: 1.5,
        thickness: 0.5,
        clearcoat: 1.0,
        clearcoatRoughness: 0.1,
      }),
      modelPath: 'models/utah_teapot.gltf',
      modelScale: 1,
    },
    modelCanvasHeightPercent: 55
  },
  {
    id: 'sea-of-serenity-theme',
    category: 'graphics',
    title: 'Sea of Serenity and Tycho Crater',
    description: 'VSCode color themes',
    gradient: 'var(--tile-grad-2)',
    details: `
      <img src="photos/screenshots/tycho-crater.png" width=100%>
      <img src="photos/screenshots/sea-of-serenity.png" width=100%>
    `,
    link: "https://github.com/webstek/sea-of-serenity"
  },
    {
    id: 'shifting-sands-theme',
    category: 'graphics',
    title: 'Shifting Sands',
    description: 'VSCode color theme',
    gradient: 'var(--tile-grad-2)',
    details: `
      <img src="photos/screenshots/screenshot2.png" width=100%>
    `,
    link: "https://github.com/webstek/shifting_sands"
  },
  {
    id: 'mathematics-glaciology',
    category: 'mathematics',
    title: 'Ice-Shelf Flow Model',
    description: 'Numerical glaciology research at UBC',
    gradient: 'var(--tile-grad-2)',
    details: `
      <p>Implemented a numerical ice-shelf flow model for improved glacial melt predictions using Python. Collaborated with a small team doing glaciology field work in a challenging environment.</p>
      <p>Independently developed a prototype solver for a 1D ice-shelf flow model, computationally solving a set of coupled nonlinear partial differential equations. Documented the software development process which was reviewed and validated with supervising professor.</p>
    `,
    model: {
      geometry: () => new THREE.TetrahedronGeometry(1.3),
      modelPath: 'models/kluane.gltf',
      modelScale: 0.075,
      modelUseMaterial: true,
    },
  },
  {
    id: 'enbridge-data-analyst',
    category: 'mathematics',
    title: 'Binary Classifier for Forecasted Project Costs',
    description: 'Data Analytics at Enbridge',
    gradient: 'var(--tile-grad-2)',
    details: `
      <p>Built a predictive model to classify project spending forecasts as consistent or inconsistent with past forecasts. The model used WoE encoding, PCA, and k-nearest neighbours to perform classification.</p>
      <p>Trained model on 7 years of data, including more than 30 features and 10 000 rows, using SQL and python for acquisition and preprocessing.</p>
    `,
  },
  {
    id: 'japan-roll1-1',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Crows (or ravens?) in a tree near Osaka Castle',
    imageSrc: 'photos/japan/10001.jpg',
    imageAlt: 'Crows or ravens in a tree new Osaka Castle',
    details: '',
    photoInfo: {
      location: 'Outside Osaka Castle, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll1-2',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Osaka Castle',
    imageSrc: 'photos/japan/10002.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Osaka Castle, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll1-4',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: '',
    imageSrc: 'photos/japan/10004.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Osaka, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll1-6',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Mt. Fuji from the Shinkansen',
    imageSrc: 'photos/japan/10006.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Near Nagoya, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll1-13',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Canal in Tokyo',
    imageSrc: 'photos/japan/10013.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Tokyo, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll1-16',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Tokyo Tower',
    imageSrc: 'photos/japan/10016.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Tokyo, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll2-3',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'View from trail at Ginkakuji',
    imageSrc: 'photos/japan/20003.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Kyoto, Japan',
      date: 'January 2025',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll2-4',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Stone cone at Ginkakuji',
    imageSrc: 'photos/japan/20004.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Kyoto, Japan',
      date: 'January 2025',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll2-7',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Sunset in Fukuoka prefecture',
    imageSrc: 'photos/japan/20007.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Fukuoka prefecture, Japan',
      date: 'January 2025',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll2-9',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Porcelain sign in Akizuki',
    imageSrc: 'photos/japan/20009.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Akizuki, Japan',
      date: 'January 2025',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll2-10',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Shrine steps in Akizuki',
    imageSrc: 'photos/japan/20010.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Akizuki, Japan',
      date: 'January 2025',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll2-14',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Chikugo river near Higaeri Onsen',
    imageSrc: 'photos/japan/20014.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Nijinoya, Japan',
      date: 'January 2025',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll2-18',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Sunset in Mitoma',
    imageSrc: 'photos/japan/20018.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Mitoma, Fukuoka prefecture, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll2-19',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Sunset cloud in Mitoma',
    imageSrc: 'photos/japan/20019.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Mitoma, Fukuoka prefecture, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll2-25',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Fukuoka Seaside Tower',
    imageSrc: 'photos/japan/20025.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Fukuoka, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll2-31',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Bird at Otori Park',
    imageSrc: 'photos/japan/20031.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Fukuoka, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll2-37',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Pagoda at Tochoji',
    imageSrc: 'photos/japan/20037.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Fukuoka, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll3-4',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Statue in Higashi Park',
    imageSrc: 'photos/japan/30004.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Fukuoka, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll3-8',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Morning walk on the Hakata River',
    imageSrc: 'photos/japan/30008.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Fukuoka, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll3-18',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Sea creature in the Osaka Aquarium',
    imageSrc: 'photos/japan/30018.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Osaka, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll3-23',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Sunset from the ferris wheel near the Osaka Aquarium',
    imageSrc: 'photos/japan/30023.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Osaka, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll3-25',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Street in Osaka',
    imageSrc: 'photos/japan/30025.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Osaka, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll4-10',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Tree in Hakone',
    imageSrc: 'photos/japan/40010.jpg',
    imageAlt: 'Japan, January 2025',
    details: '',
    photoInfo: {
      location: 'Hakone, Japan',
      date: 'January 2025',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll4-11',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Frosted grass in Hakone',
    imageSrc: 'photos/japan/40011.jpg',
    imageAlt: 'Japan, January 2025',
    details: '',
    photoInfo: {
      location: 'Hakone, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll4-12',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Snowy field in Hakone',
    imageSrc: 'photos/japan/40012.jpg',
    imageAlt: 'Japan, December 2024',
    details: '',
    photoInfo: {
      location: 'Hakone, Japan',
      date: 'December 2024',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll4-13',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Hakone street',
    imageSrc: 'photos/japan/40013.jpg',
    imageAlt: 'Japan, January 2025',
    details: '',
    photoInfo: {
      location: 'Hakone, Japan',
      date: 'January 2025',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll4-14',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Snowy bush close-up',
    imageSrc: 'photos/japan/40014.jpg',
    imageAlt: 'Japan, January 2025',
    details: '',
    photoInfo: {
      location: 'Hakone, Japan',
      date: 'January 2025',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll4-15',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Sunlit snowy bush',
    imageSrc: 'photos/japan/40015.jpg',
    imageAlt: 'Japan, January 2025',
    details: '',
    photoInfo: {
      location: 'Hakone, Japan',
      date: 'January 2025',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll4-21',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Mt. Ashigara trail',
    imageSrc: 'photos/japan/40021.jpg',
    imageAlt: 'Japan, January 2025',
    details: '',
    photoInfo: {
      location: 'Hakone, Japan',
      date: 'January 2025',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll4-22',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Sunset on Mt. Ashigara',
    imageSrc: 'photos/japan/40022.jpg',
    imageAlt: 'Japan, January 2025',
    details: '',
    photoInfo: {
      location: 'Hakone, Japan',
      date: 'January 2025',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
  {
    id: 'japan-roll4-25',
    category: 'photography',
    subcategory: 'Japan',
    title: '',
    description: 'Kintoki shrine',
    imageSrc: 'photos/japan/40025.jpg',
    imageAlt: 'Japan, January 2025',
    details: '',
    photoInfo: {
      location: 'Hakone, Japan',
      date: 'January 2025',
      camera: 'Yashica TL-Super 35mm f/1.8',
      film: 'Kodak Gold 200'
    },
  },
];
